import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';
import { Button, LoadingSpinner } from './ui';
import MemberMergeModal, { IMemberForMerge } from './MemberMergeModal';
import './DuplicateCleanupPanel.css';
import { translateError } from '../utils/errorMessages';

// =============================================================================
// TYPES
// =============================================================================

interface DuplicateSuggestion {
  members: IMemberForMerge[];
  reason: string;
}

interface CleanupResponse {
  totalExternalMembers: number;
  duplicateGroups: number;
  suggestions: DuplicateSuggestion[];
}

interface DuplicateCleanupPanelProps {
  onCleanupComplete?: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * DuplicateCleanupPanel - Panel for finding and merging duplicate external members
 *
 * Fetches duplicate suggestions from the backend and displays them
 * in groups. Users can merge or ignore each group.
 */
const DuplicateCleanupPanel: React.FC<DuplicateCleanupPanelProps> = ({
  onCleanupComplete,
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [cleanupData, setCleanupData] = useState<CleanupResponse | null>(null);
  const [ignoredGroups, setIgnoredGroups] = useState<Set<number>>(new Set());
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<DuplicateSuggestion | null>(null);

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.members.findDuplicates();
      setCleanupData(response.data);
      setIgnoredGroups(new Set());
    } catch (err: any) {
      addToast(
        translateError(err, t, 'members.cleanupError'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [addToast, t]);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const handleIgnore = (groupIndex: number) => {
    setIgnoredGroups(prev => {
      const newSet = new Set(prev);
      newSet.add(groupIndex);
      return newSet;
    });
  };

  const handleMerge = (suggestion: DuplicateSuggestion) => {
    setSelectedGroup(suggestion);
    setMergeModalOpen(true);
  };

  const handleMergeComplete = () => {
    fetchDuplicates();
    onCleanupComplete?.();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Filter out ignored groups
  const visibleSuggestions = cleanupData?.suggestions.filter(
    (_, index) => !ignoredGroups.has(index)
  ) || [];

  if (loading) {
    return (
      <div className="duplicate-cleanup-panel duplicate-cleanup-panel--loading">
        <LoadingSpinner />
        <p>{t('members.cleanupLoading')}</p>
      </div>
    );
  }

  if (!cleanupData) {
    return null;
  }

  if (visibleSuggestions.length === 0) {
    return (
      <div className="duplicate-cleanup-panel duplicate-cleanup-panel--empty">
        <div className="duplicate-cleanup-panel__empty-icon">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h3>{t('members.noDuplicatesFound')}</h3>
        <p>{t('members.noDuplicatesDescription')}</p>
        <Button variant="secondary" onClick={fetchDuplicates}>
          {t('members.refreshDuplicates')}
        </Button>
      </div>
    );
  }

  return (
    <div className="duplicate-cleanup-panel">
      <div className="duplicate-cleanup-panel__header">
        <div className="duplicate-cleanup-panel__stats">
          <span className="duplicate-cleanup-panel__stat">
            {t('members.totalUnattached')}: <strong>{cleanupData.totalExternalMembers}</strong>
          </span>
          <span className="duplicate-cleanup-panel__stat">
            {t('members.duplicateGroups')}: <strong>{visibleSuggestions.length}</strong>
          </span>
        </div>
        <Button variant="secondary" size="sm" onClick={fetchDuplicates}>
          {t('members.refreshDuplicates')}
        </Button>
      </div>

      <div className="duplicate-cleanup-panel__groups">
        {cleanupData.suggestions.map((suggestion, index) => {
          if (ignoredGroups.has(index)) return null;

          return (
            <div key={index} className="duplicate-cleanup-panel__group">
              <div className="duplicate-cleanup-panel__group-header">
                <div className="duplicate-cleanup-panel__group-reason">
                  <span className="duplicate-cleanup-panel__group-label">
                    {t('members.duplicateReason')}:
                  </span>
                  <span className="duplicate-cleanup-panel__group-reason-text">
                    {suggestion.reason}
                  </span>
                </div>
                <span className="duplicate-cleanup-panel__group-count">
                  {suggestion.members.length} {t('members.members')}
                </span>
              </div>

              <div className="duplicate-cleanup-panel__members">
                {suggestion.members.map(member => (
                  <div key={member._id} className="duplicate-cleanup-panel__member">
                    <div className="duplicate-cleanup-panel__member-main">
                      <span className="duplicate-cleanup-panel__member-name">
                        {member.name}
                      </span>
                      {(member.activityCount ?? 0) === 0 && (
                        <span className="duplicate-cleanup-panel__badge duplicate-cleanup-panel__badge--external">
                          {t('members.unattached')}
                        </span>
                      )}
                    </div>
                    <div className="duplicate-cleanup-panel__member-details">
                      {member.email && (
                        <span className="duplicate-cleanup-panel__member-detail">
                          {member.email}
                        </span>
                      )}
                      {member.phone && (
                        <span className="duplicate-cleanup-panel__member-detail">
                          {member.phone}
                        </span>
                      )}
                      {member.city && (
                        <span className="duplicate-cleanup-panel__member-detail">
                          {member.city}
                        </span>
                      )}
                      {member.dateOfBirth && (
                        <span className="duplicate-cleanup-panel__member-detail">
                          {formatDate(member.dateOfBirth)}
                        </span>
                      )}
                    </div>
                    {member.originalRegistration?.event && (
                      <div className="duplicate-cleanup-panel__member-source">
                        {t('members.fromEvent')}: {member.originalRegistration.event.name}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="duplicate-cleanup-panel__group-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleIgnore(index)}
                >
                  {t('members.ignoreDuplicate')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleMerge(suggestion)}
                >
                  {t('members.mergeDuplicates')}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <MemberMergeModal
        isOpen={mergeModalOpen}
        onClose={() => {
          setMergeModalOpen(false);
          setSelectedGroup(null);
        }}
        sourceMembers={selectedGroup?.members || []}
        onMergeComplete={handleMergeComplete}
      />
    </div>
  );
};

export default DuplicateCleanupPanel;
